import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthStore } from '../../auth/store';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, type AppColumn, gridExportMatrix } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { ExportMenu } from '../../components/AppToolbar/ExportMenu';
import { FormTitle } from '../../components/FormTitle';
import { type IslandStep } from '../../components/StepIsland/StepIsland';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import { StepHint } from '../../components/StepHint/StepHint';
import { useConfirm } from '../../components/confirm';
import { useNotify } from '../../components/notifications/useNotify';
import { runLoading } from '../../components/loading';
import { fmtLocalDateTime, fmtLocalDate } from '../../utils/datetime';
// Project Notes — the SHARED ALPACA-parity popup (standing rule 8: consume the
// shared capability, never reinvent). GoLive's ProjectNotesViewController targets
// Samplecheckin_DetailView_FieldDataEntry, so this form gets the same button; its
// SetCriteria branch for "FieldDataEntry" scopes notes to the JOB.
import { NotesListPopup, useNotesCount } from '../analysis/SdmsProjectNotes';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface FieldDataEntryRow {
  jobId:             string;
  jobOid:            string;
  client:            string;
  projectId:         string;
  projectName:       string;
  sampleMatrix:      string;
  status:            string;
  statusId:          string;
  samplingStatusInt: number;
  testCount:         number;
  receivedDate:      string | null;
  dueDate:           string | null;
  updatedDate:       string | null;
}

interface StationRow {
  sampleLoginOid:  string;
  stationId:       string;
  stationName:     string;
  blended:         boolean;
  latitude:        string | null;
  longitude:       string | null;
  temperature:     string | null;
  humidity:        string | null;
  stationComment:  string | null;
  [key: string]: unknown; // dynamic (config-driven) extra fields
}

interface SamplingRow {
  sampleLoginOid: string;
  stationId:      string;
  stationName:    string;
  sampleType:     string;
  sampleId:       string;
  collectorOid:   string | null;
  collectorName:  string | null;
  collectedDate:  string | null;
  sampleComment:  string | null;
  testSummary:    string | null;
  enteredBy:      string | null;
  enteredByOid?:  string | null; // set only when freshly stamped this session
  enteredDate:    string | null;
  isHold:         boolean;
  [key: string]: unknown; // dynamic (config-driven) extra fields
}

interface UserItem {
  userOid:  string;
  userName: string;
}

interface MonitoringRow {
  sampleParameterOid: string;
  sampleLoginOid:     string;
  sampleId:           string;
  test:               string;
  methodName:         string;
  parameter:          string;
  result:             string | null;
  unit:               string;
  monitoredBy:        string | null;
  monitoredByOid?:    string | null; // set when the user picks Monitored By
  monitoredDate:      string | null;
  enteredBy:          string | null;
  enteredByOid?:      string | null; // set only when freshly stamped this session
  enteredDate:        string | null;
  isHold:             boolean;
  [key: string]: unknown; // dynamic (config-driven) extra fields
}

type TabKey = 'stations' | 'sampling' | 'monitoring';

// ─── Field Config (SamplingFieldConfiguration) ────────────────────────────────
interface FieldConfigItem {
  fieldId:     string;
  caption:     string;
  width:       number;
  sortOrder:   number;
  freeze:      boolean;
  isMandatory: boolean;
  isReadonly:  boolean;
  fieldClass:  number; // 0=Sampling, 1=Station, 2=Monitoring
}

// ─── Dynamic (config-driven) extra columns bound to real SampleLogIn /
// SampleParameter columns — any configured field beyond the fixed typed set. ──
interface DynamicColumn {
  fieldId: string;
  caption: string;
  width: number;
  sortOrder: number;
  freeze: boolean;
  isMandatory: boolean;
  isReadonly: boolean;
  options?: string[] | null; // lookup (FK) fields → combo options
  editorType?: string;       // 'combo' | 'date' | 'checkbox' | 'number' | 'text' (from SQL type / FK)
}
interface DynamicFieldSet {
  columns: DynamicColumn[];
  values: Record<string, Record<string, string | null>>; // values[rowOid][fieldId]
}
const EMPTY_DYN: DynamicFieldSet = { columns: [], values: {} };

/** Build the dynamic (config-driven) columns bound to real SampleLogIn /
 *  SampleParameter columns. */
function buildDynamicColumns<T extends Record<string, unknown>>(dyn: DynamicFieldSet): AppColumn<T>[] {
  return dyn.columns.map(c => {
    // Editor from the column's SQL type / FK (XAF-parity): combo (FK) · date ·
    // checkbox (bit) · number · text.  Falls back to combo-if-options / text.
    const editor = c.editorType ?? ((c.options && c.options.length) ? 'combo' : 'text');
    const render = (r: T) => {
      const v = r[c.fieldId];
      if (v == null || v === '') return <span></span>;
      if (editor === 'date')     return <span>{fmtLocalDate(String(v))}</span>;
      if (editor === 'checkbox') return <span>{String(v) === 'true' ? '☑' : '☐'}</span>;
      return <span>{String(v)}</span>;
    };
    let edit: AppColumn<T>['edit'] | undefined;
    if (!c.isReadonly) {
      if (editor === 'combo')         edit = { type: 'combo', options: c.options ?? [], trigger: 'click' };
      else if (editor === 'date')     edit = { type: 'date', trigger: 'click' };
      else if (editor === 'checkbox') edit = { type: 'combo', options: ['true', 'false'], trigger: 'click' };
      else                            edit = { type: 'text', trigger: 'click' }; // text + number
    }
    return {
      field: c.fieldId,
      header: c.isMandatory ? (<>{c.caption} <span style={{ color: 'red' }}>*</span></>) : c.caption,
      headerText: c.isMandatory ? `${c.caption} *` : c.caption,
      width: c.width > 0 ? c.width : 120,
      render,
      ...(edit ? { edit } : {}),
    } as AppColumn<T>;
  });
}

/** The SortOrder each AI column key carries in config — fixed (mapped) + dynamic.
 *  Columns not in config (AI-only entry cells) fall back to 9999 → end. */
function sortOrderMap(
  config: FieldConfigItem[], fieldClass: number,
  fieldMap: Record<string, string>, dyn: DynamicFieldSet,
): Map<string, number> {
  const m = new Map<string, number>();
  config.filter(c => c.fieldClass === fieldClass).forEach(c => {
    const k = fieldMap[c.fieldId];
    if (k) m.set(k, c.sortOrder);
  });
  dyn.columns.forEach(c => m.set(c.fieldId, c.sortOrder));
  return m;
}

/** Assemble one tab's columns: merge the fixed + dynamic columns, order them ALL
 *  by config SortOrder (ALPACA parity — dynamic fields interleave, not appended),
 *  then pull the frozen ones to the front (sticky). */
function assembleTab<T extends Record<string, unknown>>(
  fixed: AppColumn<T>[], dyn: DynamicFieldSet,
  config: FieldConfigItem[], fieldClass: number, fieldMap: Record<string, string>,
): { cols: AppColumn<T>[]; sticky: number } {
  const merged = [...fixed, ...buildDynamicColumns<T>(dyn)];
  const order = sortOrderMap(config, fieldClass, fieldMap, dyn);
  merged.sort((a, b) => (order.get(a.field) ?? 9999) - (order.get(b.field) ?? 9999));
  return applyFreeze(merged, frozenFields(config, fieldClass, fieldMap, dyn));
}

/** Merge a dynamic value set onto rows keyed by `rowKey` (so the values live on
 *  the row object and become editable grid cells). */
function mergeDynamic<T extends Record<string, unknown>>(
  rows: T[], dyn: DynamicFieldSet, rowKey: (r: T) => string,
): T[] {
  if (dyn.columns.length === 0) return rows;
  return rows.map(r => {
    const v = dyn.values[rowKey(r)];
    return v ? { ...r, ...v } : r;
  });
}

/** The AI column keys the matrix marked Freeze — fixed (mapped from a legacy
 *  FieldID) + dynamic. */
function frozenFields(
  config: FieldConfigItem[], fieldClass: number,
  fieldMap: Record<string, string>, dyn: DynamicFieldSet,
): Set<string> {
  const s = new Set<string>();
  config.filter(c => c.fieldClass === fieldClass && c.freeze).forEach(c => {
    const k = fieldMap[c.fieldId];
    if (k) s.add(k);
  });
  dyn.columns.filter(c => c.freeze).forEach(c => s.add(c.fieldId));
  return s;
}

/** Move the frozen columns to the front (ALPACA `FixedStyle.Left` parity) and
 *  return the count to feed the grid's `stickyColumns` so they pin while the
 *  rest scroll horizontally. */
function applyFreeze<T>(
  cols: AppColumn<T>[], frozen: Set<string>,
): { cols: AppColumn<T>[]; sticky: number } {
  if (frozen.size === 0) return { cols, sticky: 0 };
  const fz = cols.filter(c => frozen.has(c.field));
  const rest = cols.filter(c => !frozen.has(c.field));
  // Frozen columns need a numeric width for the sticky offset maths.
  const widen = (c: AppColumn<T>): AppColumn<T> =>
    typeof c.width === 'number' ? c : { ...c, width: 120 };
  return { cols: [...fz.map(widen), ...rest], sticky: fz.length };
}

// Legacy FieldID → AI column key, per tab (FieldClass)
const SAMPLING_FIELD_MAP: Record<string, string> = {
  'StationLocation':  'stationName',
  'SampleID':         'sampleId',
  'Collector':        'collectorName',
  'CollectDate':      'collectedDate',
  'FieldTestSummary': 'testSummary',
  'samplecomment':    'sampleComment',
  'EnteredBy':        'enteredBy',
  'EnteredDate':      'enteredDate',
};
const STATION_FIELD_MAP: Record<string, string> = {
  'JobID':           'stationId',
  'StationLocation': 'stationName',
  'Blended':         'blended',
  'Latitude':        'latitude',
  'Longitude':       'longitude',
  'Temp':            'temperature',
  'Humidity':        'humidity',
  'stationcommment': 'stationComment',
};
const MONITORING_FIELD_MAP: Record<string, string> = {
  'Samplelogin':         'sampleId',
  'Test':                'test',
  'MethodName':          'methodName',
  'Parameter':           'parameter',
  'Result':              'result',
  'SampleparameterUnit': 'unit',
};

// Auto-stamped columns — never manually editable (the app fills EnteredBy /
// EnteredDate / MonitoredBy / MonitoredDate on edit, ALPACA parity).
const AUTO_MANAGED_COLS = new Set<string>(['enteredBy', 'enteredDate', 'monitoredBy', 'monitoredDate']);

/** Build Map<aiColumnKey, FieldConfigItem> for a given tab class. */
function buildConfigMap(
  config: FieldConfigItem[],
  fieldClass: number,
  fieldMap: Record<string, string>,
): Map<string, FieldConfigItem> {
  const map = new Map<string, FieldConfigItem>();
  config.filter(c => c.fieldClass === fieldClass).forEach(c => {
    const key = fieldMap[c.fieldId];
    if (key) map.set(key, c);
  });
  return map;
}

/**
 * Apply the Sample Field Configuration to a tab's columns — ALPACA
 * `FieldDataEntryViewController.customisefields` parity.
 *
 * When the job's matrix has field config for this tab, show ONLY the SELECTED
 * columns (config-driven visibility — every column not in config is hidden),
 * ordered by SortOrder, with the configured Caption / Width / read-only, and a
 * red `*` for mandatory fields. When there is no config (empty), the tab keeps
 * its default column set unchanged (matches the SP's "no matrix → defaults").
 */
function applyFieldConfig<T>(
  base: AppColumn<T>[],
  config: FieldConfigItem[],
  fieldClass: number,
  fieldMap: Record<string, string>,
): AppColumn<T>[] {
  const cfgMap = buildConfigMap(config, fieldClass, fieldMap);
  if (cfgMap.size === 0) return base; // no config → default columns

  // AI columns that ARE part of the config vocabulary (mapped from a legacy
  // FieldID). A configurable column shows ONLY when its matrix selected it;
  // AI-only columns not in the vocabulary (e.g. the Monitoring MonitoredBy /
  // MonitoredDate entry cells) are kept always so data entry never breaks.
  const configurable = new Set<string>(Object.values(fieldMap));

  const applied = base
    .filter(col => !configurable.has(col.field as string) || cfgMap.has(col.field as string))
    .map(col => {
      const cfg = cfgMap.get(col.field as string);
      if (!cfg) return col; // AI-only column — leave untouched
      const label = cfg.caption;
      let col2: AppColumn<T> = {
        ...col,
        width: cfg.width > 0 ? cfg.width : col.width,
        header: cfg.isMandatory
          ? (<>{label} <span style={{ color: 'red' }}>*</span></>)
          : label,
        headerText: cfg.isMandatory ? `${label} *` : label,
      };
      if (cfg.isReadonly) {
        const { edit: _e, ...rest } = col2 as typeof col2 & { edit?: unknown };
        col2 = rest as AppColumn<T>;
      } else if (!(col2 as { edit?: unknown }).edit && !AUTO_MANAGED_COLS.has(col.field as string)) {
        // Config says editable (IsReadonly unchecked) but the base column has no
        // editor → give it the default click-to-edit text cell (e.g. SampleComment).
        col2 = { ...col2, edit: { type: 'text', trigger: 'click' } } as AppColumn<T>;
      }
      return col2;
    });

  return applied.sort((a, b) =>
    (cfgMap.get(a.field as string)?.sortOrder ?? 9999) -
    (cfgMap.get(b.field as string)?.sortOrder ?? 9999));
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function statusPillVariant(statusId: string): string {
  if (statusId === 'FD-2-1') return 'warn';
  return 'info';
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function FieldDataEntryPage() {
  // ── List state ─────────────────────────────────────────────────────────────
  const [rows,       setRows]       = useState<FieldDataEntryRow[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [loadMs,     setLoadMs]     = useState<number | null>(null);
  const [error,      setError]      = useState<string | null>(null);
  const [search,     setSearch]     = useState('');
  // The list grid's OWN filtered+sorted rows (column filters + search) — the
  // export must reflect exactly what the grid shows, not the full dataset.
  const [listVisibleRows, setListVisibleRows] = useState<FieldDataEntryRow[]>([]);
  const searchRef = useRef<HTMLInputElement>(null);

  // ── Selected job (detail mode) ─────────────────────────────────────────────
  const [selectedJob,    setSelectedJob]    = useState<FieldDataEntryRow | null>(null);
  const [activeTab,      setActiveTab]      = useState<TabKey>('stations');
  const [selectedJobOid, setSelectedJobOid] = useState<string | null>(null);

  // ── Project Notes (GoLive ProjectNotesViewController, job scope) ────────────
  // The route lives on THIS module's controller and delegates to the shared
  // INoteReader, so the popup, the dbo.Notes storage and the author-only rule
  // (§14a Rule C) are all the existing shared ones. Hook is called
  // unconditionally (rules of hooks); with no job the URL is undefined → count 0.
  const [notesOpen, setNotesOpen] = useState(false);
  const notesUrl = selectedJob
    ? `/api/v1/field-data-entry/${selectedJob.jobOid}/project-notes`
    : undefined;
  const notesCount = useNotesCount('', null, notesUrl);

  // ── Detail data ────────────────────────────────────────────────────────────
  const [stations,     setStations]     = useState<StationRow[]>([]);
  const [sampling,     setSampling]     = useState<SamplingRow[]>([]);
  const [monitoring,   setMonitoring]   = useState<MonitoringRow[]>([]);
  // Each tab grid's OWN filtered+sorted rows — the tab's Export must reflect
  // exactly what's shown (column filters + sort), not the full set.
  const [stationVisible,    setStationVisible]    = useState<StationRow[]>([]);
  const [samplingVisible,   setSamplingVisible]   = useState<SamplingRow[]>([]);
  const [monitoringVisible, setMonitoringVisible] = useState<MonitoringRow[]>([]);
  const [fieldConfig,  setFieldConfig]  = useState<FieldConfigItem[]>([]);
  const [stationDyn,   setStationDyn]   = useState<DynamicFieldSet>(EMPTY_DYN);
  const [samplingDyn,  setSamplingDyn]  = useState<DynamicFieldSet>(EMPTY_DYN);
  const [monitoringDyn, setMonitoringDyn] = useState<DynamicFieldSet>(EMPTY_DYN);
  const [detailLoading, setDetailLoading] = useState(false);

  // ── User list for CollectedBy combo ────────────────────────────────────────
  const [users, setUsers] = useState<UserItem[]>([]);
  // Global user/employee roster for the EnteredBy combo. GoLive shows the
  // Employee DisplayName (e.g. "Service", "C16294", "E105118"). Stored value is
  // the Employee Oid (EnteredBy FK).
  const [employees, setEmployees] = useState<{ oid: string; name: string }[]>([]);

  // ── Sampling bulk-apply state ───────────────────────────────────────────────
  const [selectedSamplingIds, setSelectedSamplingIds] = useState<Set<string | number>>(new Set());
  const [bulkCollectorOid, setBulkCollectorOid] = useState('');
  const [bulkDate,         setBulkDate]         = useState('');
  const [bulkApplying] = [false]; // kept as const false — apply is now instant (local state only)

  // ── Monitoring bulk-apply state
  const [selectedMonitoringIds, setSelectedMonitoringIds] = useState<Set<string | number>>(new Set());
  const [bulkMonitoredByOid,    setBulkMonitoredByOid]    = useState('');
  const [bulkMonitoredDate,     setBulkMonitoredDate]     = useState('');
  const [bulkMonitoringApplying] = [false]; // kept as const false — apply is now instant (local state only)
  const [isDirty,     setIsDirty]     = useState(false);
  const [activeStep,  setActiveStep]  = useState<string>('job'); // tracks stepper position
  // Permission gate ported from GoLive FieldDataEntryViewController.OnActivated —
  // Save/Submit are enabled only for admins or roles with Write=true on the
  // FieldDataEntry navigation permission.
  const [canWrite, setCanWrite] = useState(true);

  // ── Station selection state (for Select All highlight only)
  const [selectedStationIds,    setSelectedStationIds]    = useState<Set<string | number>>(new Set());

  // ── Monitoring result context menu ────────────────────────────────────────
  const [ctxMenu, setCtxMenu] = useState<{ visible: boolean; x: number; y: number; row: MonitoringRow | null }>
    ({ visible: false, x: 0, y: 0, row: null });

  const closeCtxMenu = () => setCtxMenu(m => ({ ...m, visible: false }));

  const currentUser = useAuthStore(s => s.user);

  const notify = useNotify();
  const { confirm: confirmDlg } = useConfirm();

  // ── Load list ───────────────────────────────────────────────────────────────
  const loadList = useCallback(async (searchText?: string) => {
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const params: Record<string, string> = {};
      if (searchText) params.search = searchText;
      const res = await api.get('/api/v1/field-data-entry', { params });
      setRows((res.data as { data: FieldDataEntryRow[] }).data ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  // CollectedBy roster — loaded PER JOB, not once on mount. dbo.Collector is a
  // per-client roster (one row per person PER client), so the API scopes it to the
  // selected job's client; fetching it job-less returned every client's rows and the
  // combo listed the same person once per client (user report 2026-08-03).
  useEffect(() => {
    if (!selectedJob) { setUsers([]); return; }
    api.get(`/api/v1/field-data-entry/users?jobOid=${encodeURIComponent(selectedJob.jobOid)}`)
      .then(r => setUsers((r.data as { data: UserItem[] }).data ?? []))
      .catch(() => { /* non-fatal — the combo simply stays empty */ });
  }, [selectedJob]);

  // Global user roster for the EnteredBy combo (login UserName + Oid).
  useEffect(() => {
    api.get('/api/v1/lookups/employees')
      .then(r => setEmployees(((r.data as { data: { oid: string; displayName: string; userName: string }[] }).data ?? [])
        .map(e => ({ oid: e.oid, name: e.displayName || e.userName }))))
      .catch(() => { /* non-fatal — the combo simply stays empty */ });
  }, []);

  // ── Permission gate (ported from GoLive FieldDataEntryViewController.OnActivated) ──
  useEffect(() => {
    api.get('/api/v1/field-data-entry/permissions')
      .then(res => setCanWrite(Boolean((res.data as { data: { canWrite: boolean } }).data?.canWrite)))
      .catch(() => setCanWrite(false));
  }, []);

  // ── Load detail tabs ────────────────────────────────────────────────────────
  const loadDetail = useCallback(async (job: FieldDataEntryRow) => {
    setDetailLoading(true);
    try {
      const base = `/api/v1/field-data-entry/${job.jobOid}`;
      const [s, sp, m, fc, dStation, dSampling, dMonitoring] = await Promise.all([
        api.get(`${base}/stations`),
        api.get(`${base}/sampling`),
        api.get(`${base}/monitoring`),
        api.get(`${base}/field-config`),
        api.get(`${base}/dynamic-fields?fieldClass=1`),
        api.get(`${base}/dynamic-fields?fieldClass=0`),
        api.get(`${base}/dynamic-fields?fieldClass=2`),
      ]);
      const stationDynSet    = (dStation.data as { data: DynamicFieldSet }).data ?? EMPTY_DYN;
      const samplingDynSet   = (dSampling.data as { data: DynamicFieldSet }).data ?? EMPTY_DYN;
      const monitoringDynSet = (dMonitoring.data as { data: DynamicFieldSet }).data ?? EMPTY_DYN;
      setStationDyn(stationDynSet);
      setSamplingDyn(samplingDynSet);
      setMonitoringDyn(monitoringDynSet);
      // Merge dynamic values onto each row so they edit as normal grid cells.
      setStations(mergeDynamic((s.data as { data: StationRow[] }).data ?? [], stationDynSet, r => r.sampleLoginOid));
      setSampling(mergeDynamic((sp.data as { data: SamplingRow[] }).data ?? [], samplingDynSet, r => r.sampleLoginOid));
      setMonitoring(mergeDynamic((m.data as { data: MonitoringRow[] }).data ?? [], monitoringDynSet, r => r.sampleParameterOid));
      setFieldConfig((fc.data as { data: FieldConfigItem[] }).data ?? []);
    } catch {
      notify.error('Failed to load job details.');
    } finally {
      setDetailLoading(false);
    }
  }, [notify]);

  const openDetail = useCallback((job: FieldDataEntryRow) => {
    setSelectedJob(job);
    setActiveTab('stations');
    setActiveStep('stations');
    setIsDirty(false);
    void loadDetail(job);
  }, [loadDetail]);

  const closeDetail = useCallback(() => {
    setSelectedJob(null);
    setSelectedJobOid(null);
    setActiveStep('job');
    setIsDirty(false);
    setFieldConfig([]);
    setStationDyn(EMPTY_DYN);
    setSamplingDyn(EMPTY_DYN);
    setMonitoringDyn(EMPTY_DYN);
    void loadList();
  }, [loadList]);

  // ── Save (confirm → single batch SP → "Saved successfully.") ──────────────
  // All edits are local-state-only until this fires. One HTTP call replaces N.
  const handleSave = useCallback(async () => {
    if (!selectedJob) return;
    if (!canWrite) {
      notify.error('You do not have permission to save field data.');
      return;
    }
    const ok = await confirmDlg({ message: 'Do you want to Save?', tone: 'default' });
    if (!ok) return;
    try {
      const jobOid = selectedJob.jobOid;
      // Collect editable dynamic (config-driven) cell values to persist back to
      // their real SampleLogIn / SampleParameter columns.
      const dynChanges = (rows: Array<Record<string, unknown>>, dyn: DynamicFieldSet, keyOf: (r: Record<string, unknown>) => string) =>
        rows.flatMap(r => dyn.columns.filter(c => !c.isReadonly).map(c => ({
          rowOid: keyOf(r),
          fieldId: c.fieldId,
          value: r[c.fieldId] == null ? null : String(r[c.fieldId]),
        })));
      await runLoading(
        (async () => {
          await api.post('/api/v1/field-data-entry/save-batch', {
            samplingRows: sampling.map(r => ({
              sampleLoginOid: r.sampleLoginOid,
              collectorOid:   r.collectorOid   ?? null,
              collectedDate:  r.collectedDate  ?? null,
              sampleComment:  r.sampleComment  ?? null,
              enteredByOid:   r.enteredByOid ?? null,
              enteredDate:    r.enteredDate  ?? null,
            })),
            monitoringRows: monitoring.map(r => ({
              sampleParameterOid: r.sampleParameterOid,
              sampleLoginOid:     r.sampleLoginOid,
              result:             r.result ?? null,
              // Monitored By/Date are the Monitoring tab's own columns — saved only
              // when the user actually picks them (never from Collected). The name is
              // resolved to an Employee Oid server-side (MonitoredBy FK -> Employee).
              monitoredByName:    r.monitoredBy ?? null,
              monitoredDate:      r.monitoredDate ?? null,
              enteredByOid:       r.enteredByOid ?? null,
              enteredDate:        r.enteredDate  ?? null,
            })),
            stationRows: stations.map(r => ({
              sampleLoginOid: r.sampleLoginOid,
              blended:        r.blended,
              latitude:       r.latitude       ?? null,
              longitude:      r.longitude      ?? null,
              temperature:    r.temperature    ?? null,
              humidity:       r.humidity       ?? null,
              stationComment: r.stationComment ?? null,
            })),
          });
          const dynPuts: Array<Promise<unknown>> = [];
          const sCh = dynChanges(stations, stationDyn, r => String(r.sampleLoginOid));
          const spCh = dynChanges(sampling, samplingDyn, r => String(r.sampleLoginOid));
          const mCh = dynChanges(monitoring, monitoringDyn, r => String(r.sampleParameterOid));
          if (sCh.length)  dynPuts.push(api.put(`/api/v1/field-data-entry/${jobOid}/dynamic-fields`, { fieldClass: 1, changes: sCh }));
          if (spCh.length) dynPuts.push(api.put(`/api/v1/field-data-entry/${jobOid}/dynamic-fields`, { fieldClass: 0, changes: spCh }));
          if (mCh.length)  dynPuts.push(api.put(`/api/v1/field-data-entry/${jobOid}/dynamic-fields`, { fieldClass: 2, changes: mCh }));
          if (dynPuts.length) await Promise.all(dynPuts);
        })(),
        { blocking: true, label: 'Saving…' }
      );
      setIsDirty(false);
      setActiveStep('save');
      notify.success('Saved successfully.');
    } catch (err: unknown) {
      const raw = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
      const msg = typeof raw === 'string' ? raw
        : (raw as { message?: string } | undefined)?.message ?? 'Save failed. Please try again.';
      notify.error(msg);
    }
  }, [selectedJob, canWrite, sampling, monitoring, stations, stationDyn, samplingDyn, monitoringDyn, confirmDlg, notify]);

  // ── Submit ──────────────────────────────────────────────────────────────────
  // Validation order (mirrors GoLive FieldDataEntryComplete_Execute):
  //  1. Must have no unsaved changes
  //  2. At least one Sampling row selected
  //  3. All monitoring parameters for each selected sample must be selected
  //  4. Mandatory fields on selected Sampling rows: CollectedBy + CollectedDate
  //  5. Mandatory fields on selected Monitoring rows: Result + MonitoredBy + MonitoredDate
  const handleSubmit = useCallback(async () => {
    if (!selectedJob) return;
    if (!canWrite) {
      notify.error('You do not have permission to submit field data.');
      return;
    }

    const errors: string[] = [];

    // 1. Unsaved changes block submit
    if (isDirty) {
      errors.push('There are unsaved changes — please Save before submitting.');
    }

    // 2. At least one sampling row selected
    if (selectedSamplingIds.size === 0) {
      errors.push('Select at least one Sample ID in the Sampling Information tab.');
    }

    if (errors.length === 0) {
      const selectedSamplingList = sampling.filter(r => selectedSamplingIds.has(r.sampleLoginOid));
      // Hold samples are exempt from every mandatory check below — mirrors GoLive
      // FieldDataEntryViewController.cs:1133/1307
      // ("if (objSampling.SampleStatus == null || !objSampling.SampleStatus.Hold)").
      const nonHoldSamplingList = selectedSamplingList.filter(s => !s.isHold);

      // 3. For every selected non-Hold sample, ALL its monitoring parameters must be selected
      for (const s of nonHoldSamplingList) {
        const allMon = monitoring.filter(r => r.sampleLoginOid === s.sampleLoginOid);
        const selMon = allMon.filter(r => selectedMonitoringIds.has(r.sampleParameterOid));
        if (allMon.length > 0 && selMon.length < allMon.length) {
          errors.push(
            `Sample ${s.sampleId}: select all ${allMon.length} monitoring parameter(s) in ` +
            `Monitoring Records (${selMon.length} of ${allMon.length} selected).`
          );
        }
      }

      // 4. Mandatory fields — selected non-Hold Sampling rows
      for (const s of nonHoldSamplingList) {
        if (!s.collectorName) errors.push(`Sample ${s.sampleId}: CollectedBy is required.`);
        if (!s.collectedDate) errors.push(`Sample ${s.sampleId}: CollectedDate is required.`);
      }

      // 5. Mandatory fields — selected non-Hold Monitoring rows
      const selectedMonList = monitoring.filter(r => selectedMonitoringIds.has(r.sampleParameterOid) && !r.isHold);
      for (const m of selectedMonList) {
        if (!m.result)        errors.push(`${m.sampleId} – ${m.parameter}: Result is required.`);
        if (!m.monitoredBy)   errors.push(`${m.sampleId} – ${m.parameter}: MonitoredBy is required.`);
        if (!m.monitoredDate) errors.push(`${m.sampleId} – ${m.parameter}: MonitoredDate is required.`);
      }
    }

    if (errors.length > 0) {
      notify.error(
        errors[0] +
        (errors.length > 1 ? ` (${errors.length - 1} more issue${errors.length > 2 ? 's' : ''})` : '')
      );
      return;
    }

    const ok = await confirmDlg({
      message: `Submit ${selectedSamplingIds.size} sample(s) for validation? ` +
               `This will move them to Pending Validation (FD-2-3).`,
      tone: 'default',
    });
    if (!ok) return;

    try {
      setActiveStep('submit');
      const selectedOids = [...selectedSamplingIds].map(String);
      await runLoading(
        api.post(`/api/v1/field-data-entry/${selectedJob.jobOid}/submit`, {
          selectedSampleOids: selectedOids,
        }),
        { blocking: true, label: 'Submitting…' }
      );
      notify.success('Field data submitted successfully.');
      closeDetail();
    } catch (err: unknown) {
      const raw = (err as { response?: { data?: { error?: unknown } } })
        ?.response?.data?.error;
      const msg = typeof raw === 'string'
        ? raw
        : (raw as { message?: string } | undefined)?.message
          ?? 'Submit failed. Please try again.';
      notify.error(msg);
    }
  }, [
    selectedJob, isDirty, canWrite,
    selectedSamplingIds, selectedMonitoringIds,
    sampling, monitoring,
    confirmDlg, notify, closeDetail,
  ]);

  // ── Delete (GoLive FieldDataDelete_Execute parity) ───────────────────────────
  // Selection-scoped "un-enter" reset of the ticked Sampling-tab samples: clears
  // the collection / monitoring / result values and returns them to Pending Data
  // Entry (SampleLogIn → PendingCompletion, field-test SampleParameter →
  // PendingEntry with Result cleared). NOT a row delete. Confirm-only (GoLive
  // prompts no reason); audited server-side as OperationType Deleted.
  const handleDelete = useCallback(async () => {
    if (!selectedJob) return;
    if (!canWrite) { notify.error('You do not have permission to delete field data.'); return; }
    const ids = [...selectedSamplingIds].map(String);
    if (ids.length === 0) {
      notify.error('Select at least one Sample ID in the Sampling Information tab to delete.');
      return;
    }

    const ok = await confirmDlg({
      title: 'Delete Field Data',
      message: `Reset entered field data for ${ids.length} selected sample${ids.length > 1 ? 's' : ''}? `
             + 'This clears the collection, monitoring and result values and returns '
             + 'the sample(s) to Pending Data Entry. This cannot be undone.',
      tone: 'danger',
      confirmLabel: 'Delete',
    });
    if (!ok) return;

    try {
      await runLoading(
        api.post(`/api/v1/field-data-entry/${selectedJob.jobOid}/delete`, { selectedSampleOids: ids }),
        { blocking: true, label: 'Deleting…' });
      notify.success('Deleted.');
      // After delete: DESELECT every tab (rows must uncheck — user request
      // 2026-08-31) and RELOAD so the cleared values + reset Sample Status show.
      setSelectedStationIds(new Set());
      setSelectedSamplingIds(new Set());
      setSelectedMonitoringIds(new Set());
      setIsDirty(false);
      await loadDetail(selectedJob);
    } catch {
      notify.error('Delete failed. Please try again.');
    }
  }, [selectedJob, canWrite, selectedSamplingIds, confirmDlg, notify, loadDetail]);

  // ── Sampling inline cell edit → local state only (no API call, no toast) ────
  // All data is persisted when the user clicks Save.
  const handleSamplingCellCommit = useCallback((
    row: SamplingRow, col: AppColumn<SamplingRow>, value: string
  ) => {
    // Editing a row auto-stamps EnteredBy / EnteredDate for that row (ALPACA parity).
    const stamp = currentUser ? { enteredBy: currentUser.name, enteredByOid: currentUser.id, enteredDate: new Date().toISOString() } : {};
    if (col.field === 'collectorName') {
      const user = users.find(u => u.userName === value);
      if (!user) { notify.error('User not found.'); return; }
      setSampling(prev => prev.map(r => r.sampleLoginOid === row.sampleLoginOid
        ? { ...r, collectorOid: user.userOid, collectorName: user.userName, ...stamp } : r));
    } else if (col.field === 'collectedDate') {
      setSampling(prev => prev.map(r => r.sampleLoginOid === row.sampleLoginOid
        ? { ...r, collectedDate: value || null, ...stamp } : r));
    } else if (col.field === 'enteredBy') {
      // Manual EnteredBy pick — store the login name + its Employee Oid; NO auto-stamp.
      const emp = employees.find(e => e.name === value);
      setSampling(prev => prev.map(r => r.sampleLoginOid === row.sampleLoginOid
        ? { ...r, enteredBy: value, enteredByOid: emp?.oid ?? null } : r));
    } else if (col.field === 'enteredDate') {
      setSampling(prev => prev.map(r => r.sampleLoginOid === row.sampleLoginOid
        ? { ...r, enteredDate: value || null } : r));
    } else {
      setSampling(prev => prev.map(r => r.sampleLoginOid === row.sampleLoginOid
        ? { ...r, [col.field]: value, ...stamp } : r));
    }
    setIsDirty(true);
  }, [users, employees, notify, currentUser]);

  // ── Monitoring inline cell edit → local state only ──────────────────────────
  const handleMonitoringSave = useCallback((
    row: MonitoringRow, col: AppColumn<MonitoringRow>, value: string
  ) => {
    const stamp = currentUser ? { enteredBy: currentUser.name, enteredByOid: currentUser.id, enteredDate: new Date().toISOString() } : {};
    if (col.field === 'monitoredBy') {
      const mEmp = employees.find(e => e.name === value);
      setMonitoring(prev => prev.map(r =>
        r.sampleParameterOid === row.sampleParameterOid
          ? { ...r, monitoredBy: value, monitoredByOid: mEmp?.oid ?? null, ...stamp } : r));
    } else if (col.field === 'monitoredDate') {
      setMonitoring(prev => prev.map(r =>
        r.sampleParameterOid === row.sampleParameterOid ? { ...r, monitoredDate: value || null, ...stamp } : r));
    } else if (col.field === 'enteredBy') {
      const emp = employees.find(e => e.name === value);
      setMonitoring(prev => prev.map(r =>
        r.sampleParameterOid === row.sampleParameterOid ? { ...r, enteredBy: value, enteredByOid: emp?.oid ?? null } : r));
    } else if (col.field === 'enteredDate') {
      setMonitoring(prev => prev.map(r =>
        r.sampleParameterOid === row.sampleParameterOid ? { ...r, enteredDate: value || null } : r));
    } else {
      setMonitoring(prev => prev.map(r =>
        r.sampleParameterOid === row.sampleParameterOid ? { ...r, [col.field]: value, ...stamp } : r));
    }
    setIsDirty(true);
  }, [currentUser, users, employees]);

  // ── Station inline cell edit → local state only ─────────────────────────────
  const handleStationCellCommit = useCallback((
    row: StationRow, col: AppColumn<StationRow>, value: string
  ) => {
    setStations(prev => prev.map(r =>
      r.sampleLoginOid === row.sampleLoginOid ? { ...r, [col.field]: value } : r));
    setIsDirty(true);
  }, []);

  // ── Sampling row selection → auto-populate EnteredBy / EnteredDate ─────────
  // Row selection is NOT a data edit — EnteredBy / EnteredDate are stamped only
  // when a cell in the row is actually edited (see handleSamplingCellCommit /
  // handleMonitoringSave), not on checkbox select (user request 2026-08-25).
  const handleSamplingSelectionChange = useCallback((ids: Set<string | number>) => {
    setSelectedSamplingIds(ids);
  }, []);

  // Monitoring Records ONLY (user request 2026-08-25): checking a row stamps
  // EnteredBy / EnteredDate; UNchecking it clears them again. (Sampling stays
  // edit-only.) Already-selected rows keep their original stamp.
  const handleMonitoringSelectionChange = useCallback((ids: Set<string | number>) => {
    const newlyChecked   = [...ids].filter(id => !selectedMonitoringIds.has(id));
    const newlyUnchecked = [...selectedMonitoringIds].filter(id => !ids.has(id));
    setSelectedMonitoringIds(ids);
    if (newlyChecked.length || newlyUnchecked.length) {
      const now = new Date().toISOString();
      setMonitoring(prev => prev.map(r => {
        if (currentUser && newlyChecked.includes(r.sampleParameterOid))
          return { ...r, enteredBy: currentUser.name, enteredByOid: currentUser.id, enteredDate: now };
        if (newlyUnchecked.includes(r.sampleParameterOid))
          return { ...r, enteredBy: null, enteredByOid: null, enteredDate: null };
        return r;
      }));
    }
  }, [selectedMonitoringIds, currentUser]);

  // ── Sampling bulk-apply → local state only, persisted on Save ──────────────
  const handleApplyToSelected = useCallback(async () => {
    if (selectedSamplingIds.size === 0) { notify.error('Select at least one row.'); return; }
    if (!bulkCollectorOid && !bulkDate) { notify.error('Select CollectedBy or CollectedDate.'); return; }
    const ok = await confirmDlg({ message: `Apply to ${selectedSamplingIds.size} selected row(s)?`, tone: 'default' });
    if (!ok) return;
    const collectorName = users.find(u => u.userOid === bulkCollectorOid)?.userName ?? null;
    setSampling(prev => prev.map(r =>
      selectedSamplingIds.has(r.sampleLoginOid)
        ? { ...r,
            collectorOid:  bulkCollectorOid || r.collectorOid,
            collectorName: collectorName    ?? r.collectorName,
            collectedDate: bulkDate         || r.collectedDate }
        : r));
    setIsDirty(true);
    notify.success('Applied to selected rows.');
  }, [selectedSamplingIds, bulkCollectorOid, bulkDate, users, confirmDlg, notify]);


  // ── Monitoring bulk-apply → local state only, persisted on Save ─────────────
  const handleApplyMonitoringToSelected = useCallback(async () => {
    if (selectedMonitoringIds.size === 0) { notify.error('Select at least one row.'); return; }
    if (!bulkMonitoredByOid && !bulkMonitoredDate) { notify.error('Select Monitored By or Monitored Date.'); return; }
    const ok = await confirmDlg({ message: `Apply to ${selectedMonitoringIds.size} selected row(s)?`, tone: 'default' });
    if (!ok) return;
    const monitoredByName = employees.find(e => e.oid === bulkMonitoredByOid)?.name ?? null;
    setMonitoring(prev => prev.map(r =>
      selectedMonitoringIds.has(r.sampleParameterOid)
        ? { ...r,
            monitoredBy:    monitoredByName   ?? r.monitoredBy,
            monitoredByOid: bulkMonitoredByOid || r.monitoredByOid,
            monitoredDate:  bulkMonitoredDate  || r.monitoredDate }
        : r));
    setIsDirty(true);
    notify.success('Applied to selected rows.');
  }, [selectedMonitoringIds, bulkMonitoredByOid, bulkMonitoredDate, employees, monitoring, confirmDlg, notify]);

    // ── Filtered list ───────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const t = search.toLowerCase();
    return rows.filter(x =>
      x.jobId.toLowerCase().includes(t) ||
      x.client.toLowerCase().includes(t) ||
      x.projectId.toLowerCase().includes(t) ||
      x.projectName.toLowerCase().includes(t) ||
      x.sampleMatrix.toLowerCase().includes(t) ||
      x.status.toLowerCase().includes(t));
  }, [rows, search]);

  // ── Result context-menu actions ──────────────────────────────────────────────
  // ── Copy result to all / selected → local state only, persisted on Save ─────
  const handleCopyResultToAll = useCallback(() => {
    // Read the CURRENT row from state — ctxMenu.row is the snapshot captured when
    // the menu opened and may pre-date a just-typed result (fixed 2026-08-31).
    const srcId = ctxMenu.row?.sampleParameterOid;
    closeCtxMenu();
    const src = monitoring.find(r => r.sampleParameterOid === srcId);
    if (!src?.result) { notify.error('No result value to copy.'); return; }
    setMonitoring(prev => prev.map(r => ({ ...r, result: src.result })));
    setIsDirty(true);
    notify.success('Copied to all rows.');
  }, [ctxMenu.row, monitoring, notify]);

  const handleCopyResultToSelected = useCallback(() => {
    const srcId = ctxMenu.row?.sampleParameterOid;
    closeCtxMenu();
    const src = monitoring.find(r => r.sampleParameterOid === srcId);
    if (!src?.result) { notify.error('No result value to copy.'); return; }
    if (selectedMonitoringIds.size === 0) { notify.error('Select rows first.'); return; }
    setMonitoring(prev => prev.map(r =>
      selectedMonitoringIds.has(r.sampleParameterOid) ? { ...r, result: src.result } : r));
    setIsDirty(true);
    notify.success(`Copied to ${selectedMonitoringIds.size} selected row(s).`);
  }, [ctxMenu.row, monitoring, selectedMonitoringIds, notify]);

  // ── Island stepper steps ────────────────────────────────────────────────────
  // activeStep drives position: 'job' → 'stations' → 'sampling' → 'monitoring'
  //   → 'save' (set on Save success) → 'submit' (set when Submit fires)
  const stepOrder = ['job', 'stations', 'sampling', 'monitoring', 'save', 'submit'] as const;
  const islandSteps = useMemo((): IslandStep[] => {
    const idx = stepOrder.indexOf(activeStep as typeof stepOrder[number]);
    const stationsDone = selectedStationIds.size > 0;
    return [
      { id: 'job',        label: 'Select Job', state: !selectedJob ? 'active' : 'done' },
      // Stations is only 'done' once at least one station row is selected
      { id: 'stations',   label: 'Stations',   state: !selectedJob ? 'todo' : stationsDone ? 'done' : 'active' },
      { id: 'sampling',   label: 'Sampling',   state: !selectedJob ? 'todo' : idx > 2 ? 'done' : idx === 2 ? 'active' : 'todo' },
      { id: 'monitoring', label: 'Monitoring', state: !selectedJob ? 'todo' : idx > 3 ? 'done' : idx === 3 ? 'active' : 'todo' },
      { id: 'save',       label: 'Save',       state: !selectedJob ? 'optional' : idx > 4 ? 'done' : idx === 4 ? 'active' : 'optional' },
      { id: 'submit',     label: 'Submit',     state: !selectedJob ? 'todo' : idx >= 5 ? 'active' : 'todo' },
    ];
  }, [selectedJob, activeStep, selectedStationIds]);

  // ── Grid columns ────────────────────────────────────────────────────────────
  const listCols: AppColumn<FieldDataEntryRow>[] = [
    { field: 'jobId',        header: 'Job ID',       width: 110 },
    { field: 'client',       header: 'Client',       width: 180 },
    { field: 'projectId',    header: 'Project ID',   width: 130 },
    { field: 'projectName',  header: 'Project Name', width: 220 },
    { field: 'sampleMatrix', header: 'SampleMatrix', width: 130,
      text: r => r.sampleMatrix,
      render: r => r.sampleMatrix ? <span className="bl-pill info">{r.sampleMatrix}</span> : null },
    { field: 'status', header: 'Status', width: 180,
      text: r => r.status,
      render: r => <span className={`bl-pill ${statusPillVariant(r.statusId)}`}>{r.status}</span> },
    { field: 'statusId', header: 'StatusID', width: 100 },
  ];

  const stationColSet = useMemo(() => {
    const base: AppColumn<StationRow>[] = [
      { field: 'stationId',      header: 'JobID',            width: 120 },
      { field: 'stationName',    header: 'StationLocation',  width: 200 },
      { field: 'blended',        header: 'Blended',      width: 80,  align: 'center',
        text: r => r.blended ? 'true' : 'false',
        render: r => (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <input type="checkbox" checked={r.blended} readOnly
              style={{ cursor: 'default', width: 14, height: 14 }} />
          </div>
        ) },
      { field: 'latitude',       header: 'Latitude',     width: 110, edit: { type: 'text', trigger: 'click' } },
      { field: 'longitude',      header: 'Longitude',    width: 110, edit: { type: 'text', trigger: 'click' } },
      { field: 'temperature',    header: 'Temp (°F)',     width: 90,  edit: { type: 'text', trigger: 'click' } },
      { field: 'humidity',       header: 'Humidity (%)', width: 100, edit: { type: 'text', trigger: 'click' } },
      { field: 'stationComment', header: 'Comment',      width: 200, edit: { type: 'text', trigger: 'click' } },
    ];
    return assembleTab(applyFieldConfig(base, fieldConfig, 1, STATION_FIELD_MAP), stationDyn, fieldConfig, 1, STATION_FIELD_MAP);
  }, [fieldConfig, stationDyn]);

  const samplingColSet = useMemo(() => {
    const base: AppColumn<SamplingRow>[] = [
      { field: 'stationName',   header: 'StationLocation', width: 180 },
      { field: 'sampleId',      header: 'SampleID',        width: 130 },
      { field: 'collectorName',
        header: <>CollectedBy <span style={{ color: 'red' }}>*</span></>,
        headerText: 'CollectedBy *',
        width: 160,
        edit: {
          type: 'combo',
          options: users.map(u => u.userName),
          trigger: 'click',
          requiredEmpty: (r: SamplingRow) => !r.collectorName,
        } },
      { field: 'collectedDate',
        header: <>CollectedDate <span style={{ color: 'red' }}>*</span></>,
        headerText: 'CollectedDate *',
        width: 150, type: 'datetime',
        render: r => <span>{fmtLocalDateTime(r.collectedDate)}</span>,
        edit: {
          type: 'datetime',
          trigger: 'click',
          requiredEmpty: (r: SamplingRow) => !r.collectedDate,
        } },
      { field: 'testSummary',   header: 'TestSummary',     width: 360 },
      { field: 'sampleComment', header: 'SampleComment',   width: 200 },
      { field: 'enteredBy',     header: 'EnteredBy',       width: 130,
        edit: { type: 'combo', options: employees.map(e => e.name), trigger: 'click' } },
      { field: 'enteredDate',   header: 'EnteredDate',     width: 130, type: 'date',
        render: r => <span>{fmtLocalDate(r.enteredDate)}</span>,
        edit: { type: 'date', trigger: 'click' } },
    ];
    return assembleTab(applyFieldConfig(base, fieldConfig, 0, SAMPLING_FIELD_MAP), samplingDyn, fieldConfig, 0, SAMPLING_FIELD_MAP);
  }, [users, employees, fieldConfig, samplingDyn]);

  const monitoringColSet = useMemo(() => {
    const base: AppColumn<MonitoringRow>[] = [
      { field: 'sampleId',     header: 'SampleID',      width: 140, align: 'left' },
      { field: 'test',         header: 'Test',           width: 180, align: 'left' },
      { field: 'methodName',   header: 'MethodName',     width: 130, align: 'left' },
      { field: 'parameter',    header: 'Parameter',      width: 180, align: 'left' },
      { field: 'result',
        header: <>Result <span className="adg-req-star">*</span></>,
        headerText: 'Result *',
        width: 110,
        align: 'right',
        edit: { type: 'text', trigger: 'click' },
        onCellContextMenu: (e, r) =>
          setCtxMenu({ visible: true, x: e.clientX, y: e.clientY, row: r }),
      },
      { field: 'unit',         header: 'Units',          width: 100, align: 'left' },
      { field: 'monitoredBy',  header: 'MonitoredBy',    width: 130, align: 'left',
        /* MonitoredBy is an Employee (SampleLogIn.MonitoredBy FK -> Employee), so
           the picker lists Employee DisplayNames — NOT the Collector roster; a
           Collector name never matched an Employee, so it saved NULL (2026-08-31). */
        edit: { type: 'combo', options: employees.map(e => e.name), trigger: 'click' } },
      { field: 'monitoredDate', header: 'MonitoredDate', width: 150, type: 'datetime', align: 'left',
        render: r => <span>{fmtLocalDateTime(r.monitoredDate)}</span>,
        edit: { type: 'datetime', trigger: 'click' } },
      { field: 'enteredBy',    header: 'EnteredBy',      width: 130, align: 'left',
        edit: { type: 'combo', options: employees.map(e => e.name), trigger: 'click' } },
      { field: 'enteredDate',  header: 'EnteredDate',    width: 130, type: 'date', align: 'left',
        render: r => <span>{fmtLocalDate(r.enteredDate)}</span>,
        edit: { type: 'date', trigger: 'click' } },
    ];
    // Config-mapped columns show only when selected + sorted by sortOrder; the
    // AI-only entry columns (monitoredBy, monitoredDate, enteredBy, enteredDate)
    // are always kept and go after (§ applyFieldConfig).
    return assembleTab(applyFieldConfig(base, fieldConfig, 2, MONITORING_FIELD_MAP), monitoringDyn, fieldConfig, 2, MONITORING_FIELD_MAP);
  }, [users, employees, fieldConfig, monitoringDyn, setCtxMenu]);

  // ── Render ──────────────────────────────────────────────────────────────────

  // DETAIL VIEW
  if (selectedJob) {
    // Dynamic step hint per tab — matches prototype Step 2/3/4
    const detailHint = activeTab === 'stations'
      ? <><b>Step 2 · Stations.</b> <b>Select all rows</b> (header checkbox or Select All) to complete this step — the stepper turns green per tab. Click a cell to edit; GPS / weather are optional.</>
      : activeTab === 'sampling'
      ? <><b>Step 3 · Sampling.</b> <b>Select all rows</b>, then fill <b>CollectedBy</b> and <b>CollectedDate</b> — click a cell to edit.</>
      : <><b>Step 4 · Monitoring.</b> <b>Select all rows</b> and fill <b>every Result</b> — empty results are not allowed before submission (yellow cells).</>;

    return (
      <>
      <div className="bl-page">
        <div className="bl-top">
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span>
              <i className="ti ti-chevron-right" />
              <span>Sampling Management</span>
              <i className="ti ti-chevron-right" />
              <span className="dp-bc-link" style={{ cursor: 'pointer', color: 'var(--brand-accent)' }} onClick={closeDetail}>
                Field Data Entry
              </span>
              <i className="ti ti-chevron-right" />
              <span className="dp-bc-active">{selectedJob.jobId}</span>
            </div>

            {/* Title row — title left, Save/Submit/Delete right */}
            <div className="dp-title-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormTitle pageId="fielddata-entry" auditFormName="Field Data Entry"
                loadMs={detailLoading ? null : 0} loading={detailLoading}
                title={`Field Data Entry — ${selectedJob.jobId}`} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="ca-btn ca-btn-solid" onClick={handleSave}
                  disabled={!canWrite}
                  title={!canWrite ? 'You do not have permission to save field data' : undefined}>
                  <span style={{marginRight:3}}>💾</span> Save
                </button>
                <button className="ca-btn" onClick={handleSubmit}
                  disabled={!canWrite}
                  title={!canWrite ? 'You do not have permission to submit field data' : undefined}>
                  <span style={{marginRight:3}}>⬆️</span> Submit
                </button>
                <button className="ca-btn ca-btn-danger" onClick={handleDelete}
                  disabled={!canWrite || selectedSamplingIds.size === 0}
                  title={!canWrite
                    ? 'You do not have permission to delete field data'
                    : selectedSamplingIds.size === 0
                      ? 'Tick one or more Sample IDs in the Sampling Information tab to delete'
                      : undefined}>
                  <span className="tb-i tb-i-red">🗑</span> Delete
                </button>
              </div>
            </div>

            {/* Island stepper — portalled into AppShell topbar */}
            <TopbarStepper steps={islandSteps} />

            {/* Shared Project Notes popup — job-scoped via this module's route.
                businessObject is ALPACA's SourceName for this form, stamped on a
                new note's NoteSource. */}
            {notesOpen && notesUrl && (
              <NotesListPopup
                batchId={selectedJob.jobOid}
                batchTest="Field Data Entry"
                batchLabel={selectedJob.jobId}
                businessObject="Field Data Entry"
                baseUrl={notesUrl}
                onClose={() => setNotesOpen(false)}
              />
            )}

            {/* Dynamic step hint — changes per tab */}
            <StepHint tone="guidance">{detailHint}</StepHint>
          </div>
        </div>

        <div className="bl-grid-area">
          {/* Toolbar — Project Notes + Refresh + Unsaved changes (no top-level
              Export; each tab has its own Export to — user request 2026-08-31). */}
          <div style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 0 }}>
            <AppToolbar
              /* Project Notes sits FIRST, before Refresh (user request 2026-08-03).
                 Supplied through the shared toolbar's `left` slot — the sanctioned
                 place for form-specific actions (§18.6) — so it inherits the standard
                 .ca-btn styling and the canonical order is preserved. */
              left={
                <button className="ca-btn" onClick={() => setNotesOpen(true)}
                  title={`Project Notes for job ${selectedJob.jobId}`}>
                  <span style={{ marginRight: 3 }}>🔔</span> Project Notes ({notesCount})
                </button>
              }
              onRefresh={() => void loadDetail(selectedJob)}
              refreshing={detailLoading}
            />
            <span style={{ marginLeft: 8, fontSize: 11,
              fontWeight: isDirty ? 700 : 400,
              color: isDirty ? '#b45309' : '#9ca3af' }}>
              ● {isDirty ? 'Unsaved changes' : 'Saved'}
            </span>
          </div>

          {/* General Information card */}
          <div className="bl-card" style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderBottom: '1px solid var(--line, #e8eaf0)', background: 'linear-gradient(180deg,#fff,#f7f8fb)' }}>
              <span style={{ width: 4, height: 16, borderRadius: 3, background: 'var(--brand-accent, #e07b39)', display: 'inline-block', flexShrink: 0 }} />
              <span style={{ fontWeight: 700, fontSize: 12.5, color: '#15203a' }}>General Information</span>
              <span className={`bl-pill ${statusPillVariant(selectedJob.statusId)}`} style={{ marginLeft: 4 }}>{selectedJob.status}</span>
            </div>
            <div style={{ padding: '7px 12px 8px', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '3px 18px' }}>
              {([
                ['JobID',       selectedJob.jobId],
                ['Client',      selectedJob.client],
                ['SampleMatrix',selectedJob.sampleMatrix],
                ['ProjectID',   selectedJob.projectId],
                ['ProjectName', selectedJob.projectName],
                ['StatusID',    selectedJob.statusId],
              ] as [string, string][]).map(([lbl, val]) => (
                <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <label style={{ width: 96, flexShrink: 0, fontSize: 11, color: '#4a5260' }}>{lbl}</label>
                  <div style={{ flex: 1, minWidth: 0, padding: '2px 7px', border: '1px solid #e3e6ec', borderRadius: 4, fontSize: 11.5, background: '#f7f8fa', color: '#3a414e', height: 23, display: 'flex', alignItems: 'center', overflow: 'hidden', textOverflow: 'ellipsis' }}>{val || '—'}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Tab strip — Station Information / Sampling Information / Monitoring Records */}
          <div className="sa-filter-row" style={{ marginBottom: 8 }}>
            {(['stations', 'sampling', 'monitoring'] as TabKey[]).map(tab => (
              <button
                key={tab}
                className={`sa-filter-btn${activeTab === tab ? ' active' : ''}`}
                onClick={() => {
                  setActiveTab(tab);
                  // Advance stepper to the tab's step only if not already past it
                  const tabStep = tab === 'stations' ? 'stations' : tab === 'sampling' ? 'sampling' : 'monitoring';
                  setActiveStep(prev => {
                    const cur = stepOrder.indexOf(prev as typeof stepOrder[number]);
                    const next = stepOrder.indexOf(tabStep);
                    return next > cur ? tabStep : prev;
                  });
                }}
              >
                {tab === 'stations'
                  ? 'Station Information'
                  : tab === 'sampling'
                    ? (<>Sampling Information{selectedSamplingIds.size === sampling.length && sampling.length > 0 ? <span style={{color:'#22c55e',marginLeft:4,fontWeight:700}}>✓</span> : <span className="adg-req-star">*</span>}</>)
                    : (<>Monitoring Records<span className="adg-req-star">*</span></>)}
              </button>
            ))}
          </div>

          <div className="bl-card bl-card-flex">
            {activeTab === 'stations' && (<>
              {/* Station toolbar — Select All, Refresh, Export to, hint caption */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                borderBottom: '1px solid var(--line,#e8eaf0)', flexWrap: 'wrap', background: '#fafbfc' }}>
                <button className="ca-btn" style={{ fontSize: 11 }}
                  onClick={() => setSelectedStationIds(
                    selectedStationIds.size === stations.length
                      ? new Set()
                      : new Set(stations.map(r => r.sampleLoginOid)))}>
                  {selectedStationIds.size === stations.length && stations.length > 0
                    ? '☑ Deselect All' : '☐ Select All'}
                </button>
                <button className="ca-btn" onClick={() => void loadDetail(selectedJob!)}>
                  <span className="tb-i tb-i-green">🔄</span> Refresh
                </button>
                <ExportMenu filename="station-information" {...gridExportMatrix(stationVisible, stationColSet.cols)} />
                <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 8 }}>
                  Click a cell to edit in place. GPS / weather are optional; Blended marks a blended-run station.
                </span>
              </div>
              <AppDataGrid<StationRow>
                rows={stations}
                columns={stationColSet.cols}
                stickyColumns={stationColSet.sticky}
                getRowId={r => r.sampleLoginOid}
                onVisibleRowsChange={setStationVisible}
                loading={detailLoading}
                emptyText="No station records found"
                selectable
                selectedIds={selectedStationIds}
                onSelectionChange={ids => setSelectedStationIds(ids)}
                onCellCommit={handleStationCellCommit}
              />
            </>)}
            {activeTab === 'sampling' && (<>
              {/* Bulk-apply toolbar — matches legacy layout */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                borderBottom: '1px solid var(--line,#e8eaf0)', flexWrap: 'wrap', background: '#fafbfc' }}>
                <button className="ca-btn" style={{ fontSize: 11 }}
                  onClick={() => handleSamplingSelectionChange(
                    selectedSamplingIds.size === sampling.length
                      ? new Set()
                      : new Set(sampling.map(r => r.sampleLoginOid)))}>
                  {selectedSamplingIds.size === sampling.length && sampling.length > 0
                    ? '☑ Deselect All' : '☐ Select All'}
                </button>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#4a5260' }}>Collected By<span style={{ color: 'red' }}>*</span>:</label>
                <select value={bulkCollectorOid} onChange={e => setBulkCollectorOid(e.target.value)}
                  style={{ height: 26, fontSize: 11, border: '1px solid #d0d4de', borderRadius: 4,
                    padding: '0 4px', minWidth: 160 }}>
                  <option value="">— select —</option>
                  {users.map(u => (
                    <option key={u.userOid} value={u.userOid}>{u.userName}</option>
                  ))}
                </select>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#4a5260' }}>Collected Date<span style={{ color: 'red' }}>*</span>:</label>
                <input type="datetime-local" value={bulkDate} onChange={e => setBulkDate(e.target.value)}
                  style={{ height: 26, fontSize: 11, border: '1px solid #d0d4de', borderRadius: 4,
                    padding: '0 6px' }} />
                <button className="ca-btn"
                  disabled={bulkApplying || selectedSamplingIds.size === 0 || (!bulkCollectorOid && !bulkDate)}
                  onClick={() => void handleApplyToSelected()}>
                  {bulkApplying ? '…' : '✓ Apply to Selected'}
                </button>
                <button className="ca-btn" onClick={() => void loadDetail(selectedJob!)}>
                  <span className="tb-i tb-i-green">🔄</span> Refresh
                </button>
                <ExportMenu filename="sampling-information" {...gridExportMatrix(samplingVisible, samplingColSet.cols)} />
                <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7280' }}>
                  {selectedSamplingIds.size} of {sampling.length} selected
                </span>
              </div>
              <AppDataGrid<SamplingRow>
                rows={sampling}
                columns={samplingColSet.cols}
                stickyColumns={samplingColSet.sticky}
                getRowId={r => r.sampleLoginOid}
                onVisibleRowsChange={setSamplingVisible}
                loading={detailLoading}
                emptyText="No sampling records found"
                selectable
                selectedIds={selectedSamplingIds}
                onSelectionChange={handleSamplingSelectionChange}
                onCellCommit={(row, col, val) => void handleSamplingCellCommit(row, col, val)}
              />
            </>)}
            {activeTab === 'monitoring' && (<>
              {/* Monitoring bulk-apply toolbar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                borderBottom: '1px solid var(--line,#e8eaf0)', flexWrap: 'wrap', background: '#fafbfc' }}>
                <button className="ca-btn" style={{ fontSize: 11 }}
                  onClick={() => handleMonitoringSelectionChange(
                    selectedMonitoringIds.size === monitoring.length
                      ? new Set()
                      : new Set(monitoring.map(r => r.sampleParameterOid)))}>
                  {selectedMonitoringIds.size === monitoring.length && monitoring.length > 0
                    ? '☑ Deselect All' : '☐ Select All'}
                </button>
                <span style={{ width: 1, height: 20, background: '#dcdcdc', margin: '0 2px' }} />
                <label style={{ fontSize: 11, fontWeight: 600, color: '#4a5260' }}>Monitored By</label>
                <select value={bulkMonitoredByOid} onChange={e => setBulkMonitoredByOid(e.target.value)}
                  style={{ height: 26, fontSize: 11, border: '1px solid #d0d4de', borderRadius: 4, padding: '0 4px', minWidth: 140 }}>
                  <option value="">— select —</option>
                  {employees.map(e => (
                    <option key={e.oid} value={e.oid}>{e.name}</option>
                  ))}
                </select>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#4a5260' }}>Monitored Date</label>
                <input type="datetime-local" value={bulkMonitoredDate} onChange={e => setBulkMonitoredDate(e.target.value)}
                  style={{ height: 26, fontSize: 11, border: '1px solid #d0d4de', borderRadius: 4, padding: '0 6px' }} />
                <button className="ca-btn"
                  disabled={bulkMonitoringApplying || selectedMonitoringIds.size === 0 || (!bulkMonitoredByOid && !bulkMonitoredDate)}
                  onClick={() => void handleApplyMonitoringToSelected()}>
                  {bulkMonitoringApplying ? '…' : '✓ Apply to Selected'}
                </button>
                <span style={{ width: 1, height: 20, background: '#dcdcdc', margin: '0 2px' }} />
                <button className="ca-btn" onClick={() => void loadDetail(selectedJob!)}>
                  <span className="tb-i tb-i-green">🔄</span> Refresh
                </button>
                <ExportMenu filename="monitoring-records" {...gridExportMatrix(monitoringVisible, monitoringColSet.cols)} />
                <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7280' }}>
                  {selectedMonitoringIds.size} of {monitoring.length} selected
                </span>
              </div>
              <AppDataGrid<MonitoringRow>
                rows={monitoring}
                columns={monitoringColSet.cols}
                stickyColumns={monitoringColSet.sticky}
                getRowId={r => r.sampleParameterOid}
                onVisibleRowsChange={setMonitoringVisible}
                loading={detailLoading}
                emptyText="No monitoring records found"
                selectable
                selectedIds={selectedMonitoringIds}
                onSelectionChange={handleMonitoringSelectionChange}
                onCellCommit={(row, col, val) => void handleMonitoringSave(row, col, val)}
              />
            </>)}
          </div>
        </div>
      </div>

      {/* ── Result right-click context menu ─────────────────────────────── */}
      {ctxMenu.visible && (
        <>
          {/* Backdrop — click anywhere outside closes the menu */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
            onClick={closeCtxMenu}
            onContextMenu={e => { e.preventDefault(); closeCtxMenu(); }}
          />
          <div style={{
            position: 'fixed',
            left: ctxMenu.x,
            top: ctxMenu.y,
            zIndex: 9999,
            background: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            boxShadow: '0 4px 16px rgba(0,0,0,0.13)',
            minWidth: 190,
            overflow: 'hidden',
            fontSize: 13,
            fontFamily: 'inherit',
          }}>
            <button
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer',
                textAlign: 'left', color: '#1f2430' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              onClick={() => void handleCopyResultToAll()}
            >
              <i className="ti ti-copy" style={{ fontSize: 14, color: '#6b7280' }} />
              Copy to All Cells
            </button>
            <div style={{ height: 1, background: '#e5e7eb', margin: '0 8px' }} />
            <button
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer',
                textAlign: 'left', color: '#1f2430' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              onClick={() => void handleCopyResultToSelected()}
            >
              <i className="ti ti-checkbox" style={{ fontSize: 14, color: '#6b7280' }} />
              Copy to Selected Rows
            </button>
          </div>
        </>
      )}
      </>
    );
  }

  // LIST VIEW
  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Sampling Management</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Field Data Entry</span>
          </div>
          <div className="dp-title-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormTitle pageId="fielddata-entry" auditFormName="Field Data Entry" loadMs={loadMs} loading={loading} />
            <button
              className="ca-btn ca-btn-solid"
              disabled={!selectedJobOid}
              title={selectedJobOid ? 'Load selected job.' : 'Tick a Job ID to enable Load.'}
              onClick={() => {
                const job = filtered.find(r => r.jobOid === selectedJobOid);
                if (job) openDetail(job);
              }}
            >
              <span aria-hidden="true">⬇️</span>&nbsp;Load
            </button>
          </div>

          {/* Island stepper — portalled into AppShell topbar */}
          <TopbarStepper steps={islandSteps} />

          {/* Plain-text caption — no background (same pattern as Custom Reporting dp-subtitle) */}
          <div className="dp-subtitle">
            Jobs pending field-data capture. Tick a Job ID and click Load — or double-click the row.
          </div>

          {/* Step hint — orange banner (same StepHint public component as Custom Reporting) */}
          <StepHint tone="guidance">
            <b>Step 1 – Select Job.</b> Tick any Job ID, then click <b>Load</b> (the legacy "Retrieve") to open its station, sampling and monitoring data.
          </StepHint>
        </div>
      </div>

      <div className="bl-grid-area">
        {/* Search input and Refresh button on the same toolbar row */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <div className="list-search" style={{ display: 'inline-flex', alignItems: 'center', minWidth: 260, height: 32 }}>
                <span className="tb-i">🔍</span>
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
              </div>
            }
            onRefresh={() => void loadList()}
            refreshing={loading}
            exportData={{ filename: 'field-data-entry', ...gridExportMatrix(listVisibleRows, listCols) }}
          />
        </div>

        {search && filtered.length !== rows.length && (
          <div style={{ marginBottom: 6 }}>
            <span className="search-match-count">{filtered.length} match{filtered.length !== 1 ? 'es' : ''}</span>
          </div>
        )}

        <div className="bl-card bl-card-flex">
          {error ? (
            <div className="bl-err" style={{ margin: 12 }}>
              <i className="ti ti-alert-circle" /> {error}
              <button onClick={() => void loadList()} style={{ marginLeft: 10, color: 'var(--brand-accent, #e07b39)', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
            </div>
          ) : (
            <AppDataGrid<FieldDataEntryRow>
              rows={filtered}
              columns={listCols}
              getRowId={r => r.jobOid}
              onVisibleRowsChange={setListVisibleRows}
              loading={loading}
              loadMs={loadMs}
              emptyText="No pending field data entry jobs"
              selectable
              selectedIds={new Set(selectedJobOid ? [selectedJobOid] : [])}
              onSelectionChange={ids => {
                // Single-select: keep only the most recently added id
                if (ids.size === 0) { setSelectedJobOid(null); return; }
                const all = [...ids].map(String);
                const added = all.find(id => id !== selectedJobOid) ?? all[0] ?? null;
                setSelectedJobOid(added);
              }}
              onRowClick={row => setSelectedJobOid(row.jobOid)}
              onRowDoubleClick={openDetail}
            />
          )}
        </div>
      </div>
    </div>
  );
}
